'use client';
import './auth.scss';

/* eslint-disable @next/next/no-img-element */
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FilledInput,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Select from '@mui/material/Select';

import { Controller, useForm } from 'react-hook-form';

import {
  countriesList,
  ICountryList,
  isPlatformExplore,
  nameRegex,
  passwordRegex,
} from '@/constants';
import { IIndustryList, industriesWordMap, signUpIndustriesList } from '@/constants/industries';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { signUp } from '@/network/serverApis/login';

import customToaster from '../toast/CustomToaster';
import { getCookie } from 'cookies-next';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEvent } from '@/services/eventEnums';
import { identifyZohoVisitor } from '@/services/zohoSalesIQ';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { CreateAccountProps, CreateAccountFormInputs } from './interfaces';
import { getCountryFromIp, getIndustryFromEmailDomain } from '@/network/serverApis/account';
import { useShoppingCart } from '@/context/ShoppingCartContext';

export default function CreateAccount({ setAccountDrawer, t }: CreateAccountProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const pathname = usePathname();
  const { openCreateAccountDrawer, setOpenCreateAccountDrawer } = useShoppingCart();
  const [countryValue, setCountryValue] = useState<ICountryList>({
    value: null,
    name: '',
    restriction: false,
  });
  const [industryValue, setIndustryValue] = useState<IIndustryList>({
    name: '',
    value: '',
  });

  //input refs
  const emailInputRef = useRef('');

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CreateAccountFormInputs>();

  useEffect(() => {
    async function setCountryFromIp() {
      // eslint-disable-next-line require-use-server
      const countryDetail = await getCountryFromIp();

      //if no response from the API return
      if (!countryDetail?.countryCode) return;

      const countryData = countriesList.find(
        country => country.value === countryDetail.countryCode
      );

      // if no country found from the country code reutrn;
      if (!countryData) return;
      setValue('country', countryData.name);
      setCountryValue(countryData);
    }
    setCountryFromIp();
  }, [setValue]);

  const hubspotutk = getCookie('hubspotutk');

  const onSubmit = async (data: CreateAccountFormInputs) => {
    const restricted = countriesList.some(
      (item: ICountryList) => item?.name === data?.country && item.restriction
    );
    const redirect = window.location.href;
    const firstName = data?.firstName?.trim();
    const lastName = data?.lastName?.trim();
    const mixpanelSignUpData = {
      first_name: firstName,
      last_name: lastName,
      country: data?.country,
      organization: data?.organization,
      industry: data?.industry,
      email_address: data?.email,
    };
    if (restricted) {
      MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_CREATE_FAIL, {
        ...mixpanelSignUpData,
        failure_reason: 'Account creation failed. Please contact support@skywatch.com',
      });
      return customToaster('Account creation failed. Please contact support@skywatch.com');
    } else {
      let formData = { ...data, firstName, lastName, redirect };
      if (hubspotutk) {
        formData = { ...data, firstName, lastName, hubspotutk, redirect };
      }
      // eslint-disable-next-line require-use-server
      const signUpData = await signUp(formData);

      if (signUpData?.error) {
        MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_CREATE_FAIL, {
          ...mixpanelSignUpData,
          failure_reason: signUpData?.error?.message,
        });
        return customToaster(signUpData?.error?.message);
      }
      if (signUpData?.data) {
        MixpanelTracking.getInstance().identify(signUpData?.data?.id, signUpData?.data?.email);
        identifyZohoVisitor({
          id: signUpData?.data?.id,
          email: signUpData?.data?.email ?? data?.email,
          firstName,
          lastName,
          organization: data?.organization?.trim(),
          industry: signUpIndustriesList.find(item => item.value === data?.industry)?.name,
        });
        reset();
        MixpanelTracking.getInstance().track(
          MixpanelEvent.ACCOUNT_CREATE_SUCCESS,
          mixpanelSignUpData
        );
        setOpenCreateAccountDrawer(false);
        setAccountDrawer(false);
        const tempEmail = {
          email: data?.email,
        };
        localStorage && localStorage.setItem('temp-email', JSON.stringify(tempEmail));
        router.push('/verify-email');
      } else {
        MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_CREATE_FAIL, {
          ...mixpanelSignUpData,
          failure_reason:
            'Something went wrong.Please try again or reach out to support if problem persists.',
        });
        customToaster(
          'Something went wrong.Please try again or reach out to support if problem persists.'
        );
      }
    }
  };

  async function setIndustryBasedonEmailDomain(text: string) {
    text = text.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    //if email format is not valid then return

    if (!emailRegex.test(text)) return;

    // eslint-disable-next-line require-use-server
    const result = await getIndustryFromEmailDomain(text);

    if (!result?.company?.category?.industry) return;
    const industryKey = result.company.category.industry
      .split(' ')
      .find(word => industriesWordMap[word.toLocaleLowerCase() as keyof typeof industriesWordMap]);

    // if not included in result then return
    if (!industryKey) return;
    const industryResult =
      industriesWordMap[industryKey.toLocaleLowerCase() as keyof typeof industriesWordMap];

    const industry = signUpIndustriesList.find(object => object.name === industryResult);

    // if no industry found return
    if (!industry) return;

    setIndustryValue(industry);
    setValue('industry', industry.value);
  }
  const handleClickShowPassword = () => setShowPassword(prev => !prev);
  const onError = (e: any) => {
    const error =
      e?.firstName?.message ||
      e?.lastName?.message ||
      e?.password?.message ||
      e?.organization?.message ||
      (process.env.NEXT_PUBLIC_SHOW_LANGUAGE_SWITCHER === 'true' &&
        e?.preferred_language?.message) ||
      'Please fill all the input fields.';
    const formData = getValues();
    const mixpanelAccountCreationFailData = {
      first_name: formData?.firstName,
      last_name: formData?.lastName,
      country: formData?.country,
      organization: formData?.organization,
      industry: formData?.industry,
      email_address: formData?.email,
      failure_reason: error,
    };
    MixpanelTracking.getInstance().track(
      MixpanelEvent.ACCOUNT_CREATE_FAIL,
      mixpanelAccountCreationFailData
    );
    return customToaster(error);
  };
  const toggleDrawer = (open: boolean) => (event: KeyboardEvent | MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setOpenCreateAccountDrawer(open);
  };
  const defaultCountryProps = {
    options: countriesList,
    getOptionLabel: (option: ICountryList) => option?.name,
  };
  const defaultIndustryProps = {
    options: signUpIndustriesList,
    getOptionLabel: (option: IIndustryList) => t(option?.value),
  };
  const list = (anchor: 'right') => (
    <>
      <Box className="drawer-header">
        <Typography variant="h5">{t('createAccount2')}</Typography>
        <IconButton name="Create Account" onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box className="drawer-content">
          <Typography variant="body2">{t('createAccount3')}</Typography>
          <Typography variant="body2">
            {t('createAccount4')}
            <Link
              className="ml-1"
              component="button"
              onClick={() => {
                MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_SIGNIN_CLICK);
                const emailPasswordSignOnUrl = `/auth/login?returnTo=${pathname}`;
                // testing social signin feature with posthog
                if (isPlatformExplore) {
                  const socialSignInUrl = `/auth/login?returnTo=${pathname}`;
                  window.location.href = socialSignInUrl;
                } else {
                  window.location.href = emailPasswordSignOnUrl;
                }
              }}
            >
              {t('signIn')}
            </Link>
          </Typography>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: true,
              pattern: { value: nameRegex, message: t('invalidFirstName') },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('firstName')}
                variant="filled"
                onChange={(e: any) => {
                  field.onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: true, pattern: { value: nameRegex, message: t('invalidLastName') } }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('lastName')}
                variant="filled"
                onChange={(e: any) => {
                  field.onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="country"
            control={control}
            defaultValue={''}
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                disableClearable
                className="thm-autocomplete"
                {...defaultCountryProps}
                id="controlled-demo"
                {...field}
                value={countryValue}
                onChange={(e: any, newValue: any) => {
                  setCountryValue(newValue);
                  field.onChange(newValue?.name);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="filled"
                    aria-label="country-text-field"
                    label={t('country')}
                  />
                )}
              />
            )}
          />
          <Controller
            name="organization"
            control={control}
            defaultValue={''}
            rules={{
              required: true,
              minLength: { value: 2, message: t('invalidOrganization') },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('organization')}
                variant="filled"
                onChange={(e: any) => {
                  field.onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="industry"
            control={control}
            defaultValue={''}
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                disableClearable
                className="thm-autocomplete"
                {...defaultIndustryProps}
                id="controlled-demo"
                {...field}
                value={industryValue}
                onChange={(e: any, newValue: any) => {
                  setIndustryValue(newValue);
                  field.onChange(newValue?.value);
                }}
                renderInput={params => (
                  <TextField {...params} variant="filled" label={t('industry')} />
                )}
              />
            )}
          />

          {process.env.NEXT_PUBLIC_SHOW_LANGUAGE_SWITCHER === 'true' && (
            <Controller
              control={control}
              name="preferred_language"
              defaultValue={''}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl variant="filled" fullWidth {...field}>
                  <InputLabel id="demo-simple-select-label">Preferred Language</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Preferred Language"
                    value={field?.value ? field?.value : ''}
                    inputProps={{
                      'data-testid': 'add-user-role-select',
                    }}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                    }}
                    MenuProps={{
                      className: 'select-menu',
                    }}
                  >
                    <MenuItem value="en-US">English</MenuItem>
                    <MenuItem value="fr-CA">French</MenuItem>
                    {/* Add more language here */}
                  </Select>
                </FormControl>
              )}
            />
          )}

          <Divider />
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                value={emailInputRef.current}
                type="email"
                label={t('emailAddress')}
                variant="filled"
                onBlur={async event => {
                  setIndustryBasedonEmailDomain(event.target.value);
                }}
                onChange={(e: any) => {
                  emailInputRef.current = e.target.value;
                  field.onChange(e.target.value.trim());
                }}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: true,
              pattern: {
                value: passwordRegex,
                message: t('invalidPassword'),
              },
            }}
            render={({ field }) => (
              <FormControl className="input-with-icon" variant="filled">
                <InputLabel htmlFor="filled-adornment-password">{t('password')}</InputLabel>
                <FilledInput
                  {...field}
                  id="filled-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={(e: any) => {
                    field.onChange(e.target.value.trim());
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            )}
          />
        </Box>
        <Box className="drawer-footer">
          <Controller
            name="agree"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <FormControlLabel
                {...field}
                className="footer-checkbox"
                control={<Checkbox />}
                label={
                  <Typography variant="caption" display="block">
                    {t('term1')}
                    <Link href={`${process.env.NEXT_PUBLIC_TERMS_OF_SERVICES}`} target="_blank">
                      {t('accountLink1')}
                    </Link>
                    {t('and')}
                    <Link href={`${process.env.NEXT_PUBLIC_EUDLA}`} target="_blank">
                      {t('term2')}
                    </Link>
                  </Typography>
                }
              />
            )}
          />
          <Button
            name="create-skywatch-account-button"
            type="submit"
            size="large"
            variant="contained"
            fullWidth
          >
            {t('createAccount')}
          </Button>
        </Box>
      </form>
    </>
  );
  return (
    <Box>
      {(['right'] as const).map(anchor => (
        <Fragment key={anchor}>
          <Button
            name="create-skywatch-account-button"
            aria-label="create-account-btn"
            onClick={() => {
              MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_SIGN_UP_CLICK);
              const socialSignOnUrl = `/auth/login?returnTo=${pathname}?isSignUp=true`;
              window.location.href = socialSignOnUrl;
            }}
            variant="contained"
            fullWidth
          >
            {t('createAccount')}
          </Button>
          <Drawer
            ModalProps={{
              className: 'skywatch-drawer-wrapper',
            }}
            PaperProps={{ className: 'skywatch-drawer' }}
            anchor="right"
            open={openCreateAccountDrawer}
            onClose={toggleDrawer(false)}
          >
            {list(anchor)}
          </Drawer>
        </Fragment>
      ))}
    </Box>
  );
}
